import React, { createContext, useState, useCallback } from "react";

import { noop } from "lodash";
import { FormattedMessage } from "react-intl";

import Button from "@atlaskit/button-next/new";

import { Option } from "../fastshift-table-filter-dropdown/types";

import { messages } from "./messages";

export type FastshiftTableFilterContextType = {
    filters: Record<string, Option>;
    setFilter: (filterId: string, value: Option) => void;
};

interface FastshiftTableFilterProviderProps {
    onFilterClear?: () => void;
}

const defaultContextValue: FastshiftTableFilterContextType = {
    filters: {},
    setFilter: noop
};

export const FastshiftTableFilterContext =
    createContext<FastshiftTableFilterContextType>(defaultContextValue);

export const FastshiftTableFilterProvider: React.FC<
    React.PropsWithChildren<FastshiftTableFilterProviderProps>
> = ({ children, onFilterClear }) => {
    const [filters, setFiltersState] = useState<Record<string, Option>>({});

    const setFilter = useCallback(
        (key: string, value: Option) => {
            const newFilters = { ...filters, [key]: value };
            setFiltersState(newFilters);
        },
        [filters]
    );

    const clearAll = useCallback(() => {
        setFiltersState({});
        onFilterClear?.();
    }, [onFilterClear]);

    return (
        <FastshiftTableFilterContext.Provider value={{ filters, setFilter }}>
            {children}
            {Object.keys(filters).length > 0 && (
                <Button
                    appearance="subtle"
                    onClick={() => {
                        clearAll();
                    }}
                    testId="clear-filters-button"
                >
                    <FormattedMessage
                        {...messages.fastshiftTableFilterContextClearFilters}
                    />
                </Button>
            )}
        </FastshiftTableFilterContext.Provider>
    );
};
